import React from "react";
import { translateService, dateHelperService, signService } from "../../services/imports";
import shareService from "../../services/shareService";
import { LoadingButton } from "../formcontrols/imports";
import { describeSignerMatch } from "./signerMatch";

// a share its sharer can still act on: share again, a fresh link, withdraw
const ACTIVE_STATES = ["NOT_OPENED", "OPENED", "DECLINED", "EXPIRED"];
const PAGE_SIZE = 50;
const SEARCH_MAX = 254; // the backend's limit (error.shareSearchTooLong)
const SEARCH_DELAY_MS = 300;
// the next page loads this close to the end of the list, in px
const LOAD_AHEAD_PX = 120;
const OPEN_KEY = "documentPage.sharesOpen";

const readOpen = () => {
  try {
    return window.localStorage.getItem(OPEN_KEY) === "1";
  } catch (e) {
    return false;
  }
};

// The document's shares on its page (step 1 of the share list: the
// document's owner sees every share, a sharer the ones they made; the backend
// decides, actionRights.shareList shows the section). Collapsed: the counts
// (GET .../shares/summary). Open: search by the recipient's address, filters
// (permission, organization, history), the shares newest first, the next
// cursor page loaded on scrolling; a row opens to its details and, where
// canManage allows, share again, a fresh link and withdraw.
//
// props: docId, workspaceId (the read's exposed ids: the signer names come
// from its signed listing), version (a change reloads), onShareAgain(share),
// signingSummary (vsys /signings/summary: the invitations signed, which
// /shares/summary no longer counts), onChanged() after a withdrawal
export default class DocumentShares extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: readOpen(),
      summary: null,
      summaryError: null,
      q: "",
      permission: "", // "" | READ | SIGN
      inside: "", // "" | "true" | "false"
      all: false,
      shares: [],
      next: null,
      loading: false,
      error: null,
      expanded: {}, // shareId -> true
      busy: null, // `${action}:${shareId}`
      rowMessage: {}, // shareId -> { text, error }
      confirmWithdraw: null,
      listing: null, // the signed listing, parsed: signer and kind names
    };
    this.scrollRef = React.createRef();
    this.request = 0;
  }

  t = (k, ...a) => translateService.t(`shareList.${k}`, ...a);

  componentDidMount() {
    this.loadSummary();
    if (this.state.open) this.load(true);
  }

  componentDidUpdate(prev) {
    if (prev.docId !== this.props.docId || prev.version !== this.props.version) {
      this.loadSummary();
      if (this.state.open) this.load(true);
    }
  }

  componentWillUnmount() {
    this.closed = true;
    clearTimeout(this.searchTimer);
  }

  // ---- loading -------------------------------------------------------------

  loadSummary = () => {
    shareService.summary(this.props.docId).subscribe(
      (res) => !this.closed && this.setState({ summary: res.data || null, summaryError: null }),
      (e) => !this.closed && this.setState({ summaryError: signService.problemMessage(e, this.t("loadError")) })
    );
  };

  // fresh: from the first page with the current filters; else the next page.
  // An answer to a request a newer one replaced is dropped.
  load = (fresh) => {
    if (!fresh && (!this.state.next || this.state.loading)) return;
    const seq = ++this.request;
    const { q, permission, inside, all, next } = this.state;
    const query = fresh
      ? {
          all,
          pageSize: PAGE_SIZE,
          permission: permission || undefined,
          insideOrganization: inside === "" ? undefined : inside === "true",
          q: q.trim() || undefined,
        }
      : { cursor: next };
    this.setState(fresh ? { loading: true, error: null, shares: [], next: null, expanded: {} } : { loading: true, error: null });
    shareService.list(this.props.docId, query).subscribe(
      (res) => {
        if (this.closed || seq !== this.request) return;
        const page = res.data || {};
        this.setState(
          (s) => ({
            shares: [...(fresh ? [] : s.shares), ...(page.shares || [])],
            next: (page.pageInfo && page.pageInfo.next) || null,
            loading: false,
          }),
          // a short page (or none) can leave the list without a scrollbar:
          // keep reading while the end is in view and a cursor remains
          this.loadIfNearEnd
        );
      },
      (e) => {
        if (this.closed || seq !== this.request) return;
        this.setState({ loading: false, error: signService.problemMessage(e, this.t("loadError")) });
      }
    );
  };

  loadIfNearEnd = () => {
    const el = this.scrollRef.current;
    if (!el || !this.state.next || this.state.loading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_AHEAD_PX) this.load(false);
  };

  // the signer and kind names of SIGN shares, from the workspace's signed
  // listing; read once, when the first SIGN share opens
  loadListing = () => {
    if (this.listingRequested || !this.props.workspaceId) return;
    this.listingRequested = true;
    signService.getSignerListing(this.props.workspaceId).subscribe(
      (res) => !this.closed && this.setState({ listing: res.listing || null }),
      () => {}
    );
  };

  // ---- the controls ----------------------------------------------------------

  toggleOpen = () => {
    const open = !this.state.open;
    this.setState({ open });
    try {
      window.localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch (e) {
      // blocked storage: the section opens collapsed next time
    }
    if (open && !this.state.shares.length && !this.state.loading) this.load(true);
  };

  setFilter = (change) => this.setState(change, () => this.load(true));

  onSearch = (e) => {
    const q = e.target.value.slice(0, SEARCH_MAX);
    this.setState({ q });
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.load(true), SEARCH_DELAY_MS);
  };

  toggleRow = (share) => {
    this.setState((s) => ({ expanded: { ...s.expanded, [share.shareId]: !s.expanded[share.shareId] } }));
    // a signer match names no signing tool: no listing to read for it
    if (share.permission === "SIGN" && !share.signerMatch) this.loadListing();
  };

  // ---- the actions -------------------------------------------------------------

  rowMessage = (shareId, text, error = false) =>
    this.setState((s) => ({ busy: null, rowMessage: { ...s.rowMessage, [shareId]: { text, error } } }));

  freshLink = (share) => {
    this.setState({ busy: `link:${share.shareId}` });
    shareService.freshLink(this.props.docId, share.shareId).subscribe(
      (res) => {
        const at = res.data && res.data.linkExpiresAt;
        this.setState((s) => ({
          shares: s.shares.map((x) => (x.shareId === share.shareId ? { ...x, linkExpiresAt: at } : x)),
        }));
        this.rowMessage(share.shareId, this.t("linkSent", share.email));
      },
      (e) => this.rowMessage(share.shareId, signService.problemMessage(e, this.t("actionError")), true)
    );
  };

  withdraw = (share) => {
    this.setState({ busy: `withdraw:${share.shareId}`, confirmWithdraw: null });
    shareService.withdraw(this.props.docId, share.shareId).subscribe(
      () => {
        this.setState({ busy: null });
        this.loadSummary();
        this.load(true);
        if (this.props.onChanged) this.props.onChanged();
      },
      (e) => this.rowMessage(share.shareId, signService.problemMessage(e, this.t("actionError")), true)
    );
  };

  // ---- naming --------------------------------------------------------------------

  // a granted signing tool: its name and kind, as the share dialog shows it
  signerName = (uuid) => {
    const signer = ((this.state.listing && this.state.listing.signers) || []).find((s) => s.uuid === uuid);
    if (!signer) return this.t("unavailable");
    const kind = translateService.t(`signature.type_${signer.type}`);
    return kind ? `${signer.name} · ${kind}` : signer.name;
  };

  kindName = (kind) => {
    const entry = ((this.state.listing && this.state.listing.grantableKinds) || []).find((k) => k.kind === kind);
    return entry ? entry.label : kind;
  };

  date = (ms) => (ms ? dateHelperService.format(ms, dateHelperService.smallDateTimeFormat) : "");

  // the name with the address under it, the address alone, or nothing
  renderSharedBy(sharedBy) {
    if (!sharedBy || (!sharedBy.name && !sharedBy.email)) return <span className="document-shares__none">—</span>;
    return (
      <React.Fragment>
        {sharedBy.name && <span className="document-shares__name">{sharedBy.name}</span>}
        {sharedBy.email && <span className="document-shares__address">{sharedBy.email}</span>}
      </React.Fragment>
    );
  }

  // ---- rendering -----------------------------------------------------------------

  // "Invitations signed: 1 of 3", as vsys counts them (not the people who
  // signed: the wording differs on purpose); nothing when none asked to sign
  invitations = () => {
    const counts = this.props.signingSummary && this.props.signingSummary.invitations;
    if (!counts || !counts.requested) return null;
    return translateService.t("share.signingCount", counts.signed || 0, counts.requested);
  };

  renderSummary() {
    const t = this.t;
    const summary = this.state.summary;
    if (!summary) return null;
    const active = summary.active || {};
    const declined = (summary.states && summary.states.DECLINED) || 0;
    const parts = [
      active.READ ? t("countRead", active.READ) : null,
      active.SIGN ? t("countSign", active.SIGN) : null,
      active.insideOrganization ? t("countInside", active.insideOrganization) : null,
      declined ? t("countDeclined", declined) : null,
      this.invitations(),
    ].filter(Boolean);
    return (
      <span className="document-shares__summary">
        <span>{t("activeCount", active.total || 0)}</span>
        {parts.map((p) => (
          <span key={p} className="document-shares__chip">
            {p}
          </span>
        ))}
      </span>
    );
  }

  renderDetails(share) {
    const t = this.t;
    const active = ACTIVE_STATES.includes(share.state);
    const grants = [...(share.signerKinds || []).map(this.kindName), ...(share.signerIds || []).map(this.signerName)];
    const rs = share.requiredSignatory;
    const country = rs && (translateService.t(`share.country_${rs.country}`) || rs.country);
    const message = this.state.rowMessage[share.shareId];
    const busy = this.state.busy;
    // a signer match (an integration's share): its criteria, not tool names
    const signWith = share.signerMatch
      ? describeSignerMatch(share.signerMatch)
      : grants.length
      ? grants.join(", ")
      : t("ownSigners");
    const facts = [
      share.permission === "SIGN" && [t("signWith"), signWith],
      rs && [t("requiredSignatory"), `${country} · ${(rs.attributes && rs.attributes.personalNumber) || ""}`],
      share.placement && [t("placement"), t("onPage", share.placement.page)],
      share.deadline && [t("deadline"), this.date(share.deadline)],
      share.linkExpiresAt && active && [t("linkExpiresAt"), this.date(share.linkExpiresAt)],
      share.signedAt && [t("signedAt"), this.date(share.signedAt)],
      share.declinedAt && [
        t("declinedAt"),
        `${this.date(share.declinedAt)}${share.declineReason ? ` · ${share.declineReason}` : ""}`,
      ],
      share.withdrawnAt && [t("withdrawnAt"), this.date(share.withdrawnAt)],
    ].filter(Boolean);
    return (
      <div className="document-shares__details">
        {facts.length > 0 && (
          <dl className="document-shares__facts">
            {facts.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
        {share.message && (
          <p className="document-shares__message">
            <span>{t("message")}: </span>
            {share.message}
          </p>
        )}
        {message && <p className={message.error ? "document-shares__error" : "document-shares__success"}>{message.text}</p>}
        {share.canManage && active && (
          <div className="buttons">
            <LoadingButton
              type="button"
              className="button background-color-two color-white"
              disabled={!!busy}
              onClick={() => this.props.onShareAgain(share)}
            >
              {t("shareAgain")}
            </LoadingButton>
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              loading={busy === `link:${share.shareId}`}
              disabled={!!busy}
              onClick={() => this.freshLink(share)}
            >
              {t("freshLink")}
            </LoadingButton>
            {this.state.confirmWithdraw === share.shareId ? (
              <React.Fragment>
                <span className="document-shares__confirm">{t("withdrawConfirm", share.email)}</span>
                <LoadingButton
                  type="button"
                  className="button background-color-three color-white"
                  loading={busy === `withdraw:${share.shareId}`}
                  disabled={!!busy}
                  onClick={() => this.withdraw(share)}
                >
                  {t("withdrawYes")}
                </LoadingButton>
                <LoadingButton
                  type="button"
                  className="button background-color-one color-white"
                  disabled={!!busy}
                  onClick={() => this.setState({ confirmWithdraw: null })}
                >
                  {translateService.t("common.cancell")}
                </LoadingButton>
              </React.Fragment>
            ) : (
              <LoadingButton
                type="button"
                className="button background-color-three color-white"
                disabled={!!busy}
                onClick={() => this.setState({ confirmWithdraw: share.shareId })}
              >
                {t("withdraw")}
              </LoadingButton>
            )}
          </div>
        )}
      </div>
    );
  }

  renderRow = (share, index) => {
    const t = this.t;
    const open = !!this.state.expanded[share.shareId];
    const detailsId = `document-share-${share.shareId}`;
    return (
      <tbody key={share.shareId} className={index % 2 === 0 ? "bg" : ""}>
        <tr>
          <td className="document-shares__recipient">
            <button
              type="button"
              className="document-shares__expand"
              aria-expanded={open}
              aria-controls={detailsId}
              aria-label={t(open ? "hideDetails" : "showDetails")}
              title={t(open ? "hideDetails" : "showDetails")}
              onClick={() => this.toggleRow(share)}
            >
              {open ? "−" : "+"}
            </button>
            <span className="document-shares__address">{share.email}</span>
            {share.insideOrganization && <span className="document-shares__chip">{t("insideOrganization")}</span>}
          </td>
          <td data-label={t("permission")}>{translateService.t(`shareDialog.permission_${share.permission}`)}</td>
          <td data-label={t("colState")}>
            <span className={`document-shares__state state-${share.state}`}>{t(`state_${share.state}`)}</span>
            {/* set exactly when someone has signed through the share */}
            {share.signedAt && <span className="document-shares__signed-on">{t("signedOn", this.date(share.signedAt))}</span>}
          </td>
          <td data-label={t("colSharedBy")} className="document-shares__by">
            {this.renderSharedBy(share.sharedBy)}
          </td>
          <td data-label={t("createdAt")} className="nowrap">
            {this.date(share.createdAt)}
          </td>
        </tr>
        {open && (
          <tr className="document-shares__details-row" id={detailsId}>
            <td colSpan={5}>{this.renderDetails(share)}</td>
          </tr>
        )}
      </tbody>
    );
  };

  renderList() {
    const t = this.t;
    const { shares, loading, error, q, permission, inside, all, summary } = this.state;
    const showOrganization = inside !== "" || !!(summary && summary.active && summary.active.insideOrganization);
    return (
      <div className="document-shares__body" id="document-shares-body">
        <div className="document-shares__tools">
          <input
            type="search"
            className="document-shares__search"
            value={q}
            maxLength={SEARCH_MAX}
            placeholder={t("searchPlaceholder")}
            aria-label={t("searchPlaceholder")}
            onChange={this.onSearch}
          />
          <div className="document-shares__filter" role="group" aria-label={t("permission")}>
            {[
              ["", "filterAll"],
              ["READ", "filterRead"],
              ["SIGN", "filterSign"],
            ].map(([value, key]) => (
              <button
                type="button"
                key={key}
                aria-pressed={permission === value}
                className={permission === value ? "is-on" : ""}
                onClick={() => permission !== value && this.setFilter({ permission: value })}
              >
                {t(key)}
              </button>
            ))}
          </div>
          {showOrganization && (
            <select
              value={inside}
              aria-label={t("orgFilter")}
              onChange={(e) => this.setFilter({ inside: e.target.value })}
            >
              <option value="">{t("orgAll")}</option>
              <option value="true">{t("orgInside")}</option>
              <option value="false">{t("orgOutside")}</option>
            </select>
          )}
          <label className="document-shares__history">
            <input type="checkbox" checked={all} onChange={(e) => this.setFilter({ all: e.target.checked })} />
            {t("includeHistory")}
          </label>
        </div>
        {error && (
          <p className="document-shares__error" role="alert">
            {error}
          </p>
        )}
        <div className="document-shares__scroll" ref={this.scrollRef} onScroll={this.loadIfNearEnd}>
          {shares.length > 0 && (
            <table className="document-shares__table">
              <thead>
                <tr>
                  <th scope="col">{t("colRecipient")}</th>
                  <th scope="col">{t("permission")}</th>
                  <th scope="col">{t("colState")}</th>
                  <th scope="col">{t("colSharedBy")}</th>
                  <th scope="col">{t("createdAt")}</th>
                </tr>
              </thead>
              {shares.map(this.renderRow)}
            </table>
          )}
          {!loading && !error && !shares.length && <p className="document-shares__empty">{t("noMatch")}</p>}
          {loading && <img src="./assets/images/black-loading.svg" className="content-loader" alt="" />}
        </div>
      </div>
    );
  }

  render() {
    const t = this.t;
    const { open, summary, summaryError } = this.state;
    // nothing to show for a document no share was ever made of (by this caller)
    if (summary && summary.all && !summary.all.total) return null;
    return (
      <section className={`document-shares ${translateService.key}`} aria-labelledby="document-shares-title">
        <button
          type="button"
          className="document-shares__toggle"
          aria-expanded={open}
          aria-controls="document-shares-body"
          onClick={this.toggleOpen}
        >
          <span id="document-shares-title" className="document-shares__title">
            {t("sectionTitle")}
          </span>
          {this.renderSummary()}
          {summaryError && <span className="document-shares__error">{summaryError}</span>}
          <span aria-hidden="true" className="document-shares__chevron">
            {open ? "−" : "+"}
          </span>
        </button>
        {open && this.renderList()}
      </section>
    );
  }
}
